/**
 * "Writes to": the tracks a step of the cycle configure wizard saves to.
 *
 * A cycle has no criteria, ranges or people of its own; its tracks do (canon 28).
 * Each step lists the cycle's open tracks: the ones it may write to are ticked by
 * default and can be unticked; a track whose content is locked, or that the person
 * may not change, is shown with the reason and no tick box (build contract §2.8).
 */
import { Lock } from 'lucide-react';
import type { WizardTrackTarget } from '../../../lib/cycleWizardTargets';

export function CycleTrackTargets({
  targets,
  chosenIds,
  onToggle,
}: {
  targets: readonly WizardTrackTarget[];
  chosenIds: readonly string[];
  onToggle: (trackId: string) => void;
}) {
  return (
    <section aria-label="Writes to" className="border border-[var(--rule)] bg-[var(--surface)] p-3 space-y-2">
      <p className="font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">Writes to</p>
      {targets.length === 0 ? (
        <p className="text-sm text-[var(--text-sec)]">This cycle has no open tracks, so there is nothing to write to yet.</p>
      ) : (
        <ul className="space-y-1">
          {targets.map((t) => (
            <li key={t.trackId} className="flex items-start gap-2 text-sm">
              {t.writable ? (
                <label className="flex items-center gap-2 cursor-pointer text-[var(--text-pri)]">
                  <input
                    type="checkbox"
                    checked={chosenIds.includes(t.trackId)}
                    onChange={() => onToggle(t.trackId)}
                  />
                  {t.trackName}
                </label>
              ) : (
                <span className="flex items-start gap-2 text-[var(--text-sec)]" data-testid={`track-not-writable-${t.trackId}`}>
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    <span className="text-[var(--text-pri)]">{t.trackName}</span>
                    {t.reason ? ` — ${t.reason}` : null}
                  </span>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** What a save did, track by track: one line per refusal, so nothing fails quietly. */
export interface TrackWriteFailure {
  trackName: string;
  what: string;
  error: string;
}

export function TrackWriteReport({ saved, failures }: { saved: number; failures: readonly TrackWriteFailure[] }) {
  return (
    <div role="status" className="space-y-1 text-sm">
      {saved > 0 && <p className="text-[var(--green)]">Saved {saved} change{saved === 1 ? '' : 's'} to the tracks.</p>}
      {failures.map((f, i) => (
        <p key={i} className="text-[var(--coral)]">
          {f.trackName}: {f.what} not saved — {f.error}
        </p>
      ))}
    </div>
  );
}
